/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from "@emotion/core";
import { StreamingLiveCommerceCreditPaymentV2 } from "components/streaming/liveCommerce/StreamingLiveCommerceCreditPaymentV2";
import { SETTLEMENT_FEE_DATA } from "constants/purchase";
import { useI18n } from "hooks/i18n/i18n";
import React from "react";
import { useSelector } from "store";

import type { PayType } from "@spwn/types";

type Props = {
  paymentMethod: PayType;
  selectedCreditCardSeq: string | null;
  selectedSecurityCode: string | null;
  setPaymentMethod: (method: PayType) => void;
  setSelectedCreditCardSeq: (cardSeq: string | null) => void;
  setSelectedSecurityCode: (code: string | null) => void;
};
export const StreamingLiveCommercePaymentMethodV2: React.FC<Props> = ({
  paymentMethod,
  selectedCreditCardSeq,
  selectedSecurityCode,
  setPaymentMethod,
  setSelectedCreditCardSeq,
  setSelectedSecurityCode,
}) => {
  const isDarkModeEnabled = useSelector(
    (state) => state.streaming.streamingSettings.isDarkModeEnabled
  );

  const { t } = useI18n();

  const classesLiveCommercePaymentMethodDesign =
    liveCommercePaymentMethodDesign({
      isDarkModeEnabled,
    });
  return (
    <React.Fragment>
      <p css={classesLiveCommercePaymentMethodDesign.paymentMethodLabel}>
        {t("streaming.commerceCart.payment")}
      </p>
      <ul css={classesLiveCommercePaymentMethodDesign.paymentMethodList}>
        <li>
          <input
            type="radio"
            name="paymentMethod"
            className="radio-input"
            id={`payment-card`}
            defaultChecked={paymentMethod === "Card"}
            onChange={() => setPaymentMethod("Card")}
          />
          <label htmlFor={`payment-card`}>
            {t("streaming.commerceCart.paymentCard")}
            <br />({t("streaming.commerceCart.settlementFee")} ¥
            {SETTLEMENT_FEE_DATA["Card"].toLocaleString()})
          </label>
          {paymentMethod === "Card" && (
            <StreamingLiveCommerceCreditPaymentV2
              isDarkModeEnabled={isDarkModeEnabled}
              selectedCreditCardSeq={selectedCreditCardSeq}
              selectedSecurityCode={selectedSecurityCode}
              setSelectedCreditCardSeq={setSelectedCreditCardSeq}
              setSelectedSecurityCode={setSelectedSecurityCode}
            />
          )}
        </li>
        <li>
          <input
            type="radio"
            name="paymentMethod"
            className="radio-input"
            id={`payment-cvs`}
            defaultChecked={paymentMethod === "CVS"}
            onChange={() => setPaymentMethod("CVS")}
          />
          <label htmlFor={`payment-cvs`}>
            {t("streaming.commerceCart.paymentCvs")}
            <br />({t("streaming.commerceCart.settlementFee")} ¥
            {SETTLEMENT_FEE_DATA["CVS"].toLocaleString()})
          </label>
        </li>
        <li>
          <input
            type="radio"
            name="paymentMethod"
            className="radio-input"
            id={`payment-phone`}
            defaultChecked={paymentMethod === "Phone"}
            onChange={() => setPaymentMethod("Phone")}
          />
          <label htmlFor={`payment-phone`}>
            {t("streaming.commerceCart.paymentPhone")}
            <br />({t("streaming.commerceCart.settlementFee")} ¥
            {SETTLEMENT_FEE_DATA["Phone"].toLocaleString()})
          </label>
        </li>
      </ul>
    </React.Fragment>
  );
};
type LiveCommercePaymentMethodDesignProps = {
  isDarkModeEnabled: boolean;
};

const liveCommercePaymentMethodDesign = (
  props: LiveCommercePaymentMethodDesignProps
) => {
  const { isDarkModeEnabled } = props;
  const textColorInDarkMode = isDarkModeEnabled
    ? `
      color: #FFFFFF;
    `
    : `
      color: #000000;
    `;
  const paymentMethodLabel = css`
    margin-bottom: 8px;
    ${textColorInDarkMode}
    font-size: 14px;
  `;
  const paymentMethodList = css`
    li {
      ${textColorInDarkMode}
      font-size: 14px;
      background-color: ${props?.isDarkModeEnabled ? "#333333" : "#f5f5f5"};
      &:not(:last-child) {
        margin-bottom: 16px;
      }
    }
    .radio-input {
      display: none;
    }
    .radio-input + label {
      position: relative;
      display: flex;
      align-items: center;
      width: 100%;
      padding: 16px 20px 16px 60px;
      cursor: pointer;
      color: ${props?.isDarkModeEnabled ? "#FFFFFF" : "#000000"};
      border-radius: 4px;
      transition: background-color 0.2s;
    }
    .radio-input + label::before {
      content: "";
      position: absolute;
      top: 50%;
      left: 20px;
      transform: translateY(-50%);
      width: 24px;
      height: 24px;
      background-color: transparent;
      border: 2px solid ${props?.isDarkModeEnabled ? "#FFFFFF" : "#000000"};
      border-radius: 50%;
      box-sizing: border-box;
    }
    .radio-input:checked + label:before {
      background-color: ${props?.isDarkModeEnabled ? "#FFFFFF" : "#000000"};
    }
    .radio-input:checked + label::after {
      content: "";
      position: absolute;
      top: 50%;
      left: 29px;
      transform: translateY(-50%);
      width: 6px;
      height: 6px;
      background-color: ${props?.isDarkModeEnabled ? "#000000" : "#FFFFFF"};
      border-radius: 50%;
    }
  `;

  return {
    paymentMethodList,
    paymentMethodLabel,
  };
};
